import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  getDailyPageErrorMessage,
  getDailyPagePayload,
  getWheelConfig,
  isDailyPageApiSuccess,
  putWheelConfig,
} from '../../services/dailyPageApi';
import { emptyPrize, weightPercents } from './dailyTasksUtils';

function WheelSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [spinsPerDay, setSpinsPerDay] = useState(1);
  const [isActive, setIsActive] = useState(true);
  const [prizes, setPrizes] = useState([emptyPrize(1)]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getWheelConfig();
      if (!isDailyPageApiSuccess(res)) {
        setError(getDailyPageErrorMessage(res, 'Failed to load wheel config.'));
        return;
      }
      const data = getDailyPagePayload(res) || {};
      const wheel = data.wheel || data;
      setSpinsPerDay(Number(wheel.spins_per_day ?? 1));
      setIsActive(wheel.is_active !== false);
      setPrizes(
        Array.isArray(wheel.prizes) && wheel.prizes.length
          ? wheel.prizes.map((p, i) => ({
              id: p.id || `seg_${i + 1}`,
              label: p.label ?? '',
              image_url: p.image_url ?? null,
              fan_energy: Number(p.fan_energy ?? 0),
              weight: Number(p.weight ?? 0),
            }))
          : [emptyPrize(1)]
      );
    } catch (err) {
      setError(err.message || 'Failed to load wheel.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const percents = weightPercents(prizes);

  const updatePrize = (index, patch) => {
    setPrizes((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const body = {
        spins_per_day: Math.max(0, Number(spinsPerDay) || 0),
        is_active: !!isActive,
        prizes: prizes.map((p) => ({
          id: p.id,
          label: p.label?.trim() || null,
          image_url: p.image_url?.trim() || null,
          fan_energy: Number(p.fan_energy) || 0,
          weight: Number(p.weight) || 0,
        })),
      };
      const res = await putWheelConfig(body);
      if (!isDailyPageApiSuccess(res)) {
        setError(getDailyPageErrorMessage(res, 'Failed to save wheel.'));
        return;
      }
      setSnackbar({ open: true, message: 'Wheel config saved.', severity: 'success' });
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Spin the wheel is <strong>global</strong> (not date-specific). Spin does{' '}
        <strong>not</strong> count toward daily progress or group FE. Also gated by feature flag{' '}
        <code>dailies_spin</code>.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box display="flex" gap={3} alignItems="center" mb={2} flexWrap="wrap">
        <TextField
          type="number"
          label="Spins per day"
          value={spinsPerDay}
          onChange={(e) => setSpinsPerDay(e.target.value)}
          inputProps={{ min: 0 }}
          sx={{ width: 160 }}
          helperText="Manager sample often 1"
        />
        <FormControlLabel
          control={
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />
          }
          label={isActive ? 'Wheel active' : 'Wheel inactive'}
        />
        <Box flexGrow={1} />
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Save wheel
        </Button>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1">Wedges</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setPrizes((prev) => [...prev, emptyPrize(prev.length + 1)])}
        >
          Add wedge
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Id</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Image URL</TableCell>
            <TableCell>FE</TableCell>
            <TableCell>Weight</TableCell>
            <TableCell>%</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {prizes.map((prize, idx) => (
            <TableRow key={`${prize.id}-${idx}`}>
              <TableCell>
                <TextField
                  size="small"
                  value={prize.id}
                  onChange={(e) => updatePrize(idx, { id: e.target.value })}
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  value={prize.label || ''}
                  onChange={(e) => updatePrize(idx, { label: e.target.value })}
                  placeholder="or leave blank if image"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  value={prize.image_url || ''}
                  onChange={(e) => updatePrize(idx, { image_url: e.target.value })}
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={prize.fan_energy}
                  onChange={(e) => updatePrize(idx, { fan_energy: Number(e.target.value) })}
                  sx={{ width: 90 }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={prize.weight}
                  onChange={(e) => updatePrize(idx, { weight: Number(e.target.value) })}
                  sx={{ width: 90 }}
                />
              </TableCell>
              <TableCell>{percents[idx]}%</TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  disabled={prizes.length <= 1}
                  onClick={() => setPrizes((prev) => prev.filter((_, i) => i !== idx))}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default WheelSettingsPage;
